import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  UnfoldMore as UnfoldMoreIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { adsAPI, applicationsAPI } from '../services/api';

// Типы рекламных блоков
const AD_TYPES = [
  { value: 'ads_banners', label: 'ads_banners' },
  { value: 'ads_banners_tv', label: 'ads_banners_tv' },
  { value: 'ads_interstitial', label: 'ads_interstitial' },
  { value: 'ads_interstitial_tv', label: 'ads_interstitial_tv' },
  { value: 'ads_interstitial_native_tv', label: 'ads_interstitial_native_tv' },
  { value: 'ads_interstitial_more', label: 'ads_interstitial_more' },
  { value: 'ads_feed', label: 'ads_feed' },
  { value: 'ads_native', label: 'ads_native' },
  { value: 'ads_native_tv', label: 'ads_native_tv' },
  { value: 'ads_start', label: 'ads_start' },
  { value: 'ads_instream', label: 'ads_instream' },
  { value: 'ads_instream_tv', label: 'ads_instream_tv' }
];

// SDK типы
const SDK_TYPES = [
  { value: 'yandex', label: 'Yandex' },
  { value: 'mytarget', label: 'MyTarget' },
  { value: 'google', label: 'Google AdMob' },
  { value: 'ima', label: 'Google IMA' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'unity', label: 'Unity Ads' }
];

// Список стран для ГЕО-ограничений
const COUNTRIES = [
  { code: 'RU', name: 'Россия' },
  { code: 'US', name: 'США' },
  { code: 'DE', name: 'Германия' },
  { code: 'FR', name: 'Франция' },
  { code: 'GB', name: 'Великобритания' },
  { code: 'CN', name: 'Китай' },
  { code: 'JP', name: 'Япония' },
  { code: 'KR', name: 'Южная Корея' },
  { code: 'IN', name: 'Индия' },
  { code: 'BR', name: 'Бразилия' }
];

// Компонент заголовка столбца с фильтрацией и сортировкой
const FilterableTableHeader = ({ 
  label, 
  field, 
  sortField, 
  sortDirection, 
  onSort, 
  filterValue = [],
  onFilterChange,
  options = []
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState('');
  const inputRef = useRef(null);

  // Возвращаем фокус на инпут после изменения searchText (когда пользователь печатает)
  useEffect(() => {
    if (anchorEl && inputRef.current && searchText !== '') {
      // Небольшая задержка чтобы не мешать нативному поведению
      const timer = setTimeout(() => {
        if (inputRef.current && document.activeElement !== inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [searchText]);

  const handleClick = (event) => {
    event.stopPropagation();
    const wasOpen = Boolean(anchorEl);
    setAnchorEl(event.currentTarget);
    
    // Фокусируем инпут только если меню не было открыто (новое открытие)
    if (!wasOpen) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText('');
  };

  const handleToggleOption = (value) => {
    const currentValues = Array.isArray(filterValue) ? filterValue : [];
    if (currentValues.includes(value)) {
      onFilterChange(currentValues.filter(v => v !== value));
    } else {
      onFilterChange([...currentValues, value]);
    }
  };

  const handleClearAll = () => {
    onFilterChange([]);
  };

  const handleSelectAll = () => {
    const allValues = filteredOptions.map(opt => opt.value);
    onFilterChange(allValues);
  };

  // Переключение состояния сортировки: none -> asc -> desc -> none
  const handleSortClick = () => {
    if (sortField !== field) {
      onSort(field, 'asc');
    } else if (sortDirection === 'asc') {
      onSort(field, 'desc');
    } else {
      onSort(null, null);
    }
  };

  // Фильтруем опции по поисковому тексту
  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(searchText.toLowerCase())
  );

  const isSorted = sortField === field;
  const selectedCount = Array.isArray(filterValue) ? filterValue.length : 0;
  const allSelected = filteredOptions.length > 0 && filteredOptions.every(opt => filterValue.includes(opt.value));

  return (
    <TableCell sx={{ p: 1, minWidth: 100, maxWidth: 150 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Название столбца и кнопка сортировки */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', flexShrink: 0 }}>
            {label}
          </Typography>
          <IconButton
            size="small"
            onClick={handleSortClick}
            sx={{ 
              p: 0.25,
              ml: 'auto'
            }}
          >
            {!isSorted && <UnfoldMoreIcon sx={{ fontSize: 16, color: 'action.disabled' }} />}
            {isSorted && sortDirection === 'asc' && <ArrowUpwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
            {isSorted && sortDirection === 'desc' && <ArrowDownwardIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
          </IconButton>
        </Box>

        {/* Инпут для поиска и фильтрации */}
        <TextField
          inputRef={inputRef}
          size="small"
          placeholder={selectedCount > 0 ? `(${selectedCount})` : 'Поиск...'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={handleClick}
          autoComplete="off"
          InputProps={{
            endAdornment: selectedCount > 0 ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAll();
                  }}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
              backgroundColor: selectedCount > 0 ? 'primary.light' : 'transparent'
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        />

        {/* Dropdown меню с чекбоксами */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          MenuListProps={{
            'aria-labelledby': 'basic-button',
            onKeyDown: (e) => {
              // Разрешаем печатать в инпуте даже когда меню открыто
              if (e.key !== 'Escape' && e.key !== 'Tab') {
                e.stopPropagation();
              }
            }
          }}
          PaperProps={{
            sx: { maxHeight: 400, minWidth: 220, maxWidth: 320 }
          }}
        >
          {/* Чекбокс "Выбрать все" */}
          {filteredOptions.length > 0 && (
            <MenuItem 
              onClick={allSelected ? handleClearAll : handleSelectAll}
              sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'grey.50' }}
            >
              <Checkbox 
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                size="small"
                sx={{ py: 0 }}
              />
              <ListItemText 
                primary={allSelected ? 'Снять все' : 'Выбрать все'}
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </MenuItem>
          )}

          {/* Список опций */}
          {filteredOptions.length === 0 ? (
            <MenuItem disabled>
              <ListItemText primary={searchText ? 'Ничего не найдено' : 'Нет данных'} />
            </MenuItem>
          ) : (
            filteredOptions.map((option, index) => {
              const isChecked = Array.isArray(filterValue) && filterValue.includes(option.value);
              return (
                <MenuItem 
                  key={index} 
                  onClick={() => handleToggleOption(option.value)}
                  dense
                >
                  <Checkbox 
                    checked={isChecked}
                    size="small"
                    sx={{ py: 0 }}
                  />
                  <ListItemText 
                    primary={option.label} 
                    primaryTypographyProps={{ fontSize: '0.875rem' }}
                  />
                </MenuItem>
              );
            })
          )}
        </Menu>
      </Box>
    </TableCell>
  );
};

// Компонент карточки рекламного блока для мобильных устройств
const AdBlockCard = ({ adBlock, applications, onEdit, onToggleStatus, onDelete, getAdTypeLabel, getSdkLabel, getGeoLabel, getApplicationsLabel }) => {
  return (
    <Card sx={{ 
      mb: 2, 
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка: название и статус */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 1,
          width: '100%'
        }}>
          {/* Название */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ 
              fontSize: '0.9rem', 
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {adBlock.name}
            </Typography>
          </Box>

          {/* Статус */}
          <Chip
            label={adBlock.is_active ? 'Активен' : 'Отключен'}
            color={adBlock.is_active ? 'success' : 'default'}
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        {/* Вторая строка: тип и SDK */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Chip 
            label={getAdTypeLabel(adBlock.ad_type)} 
            size="small" 
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
          <Chip 
            label={getSdkLabel(adBlock.type_sdk)} 
            size="small"
            sx={{ fontSize: '0.7rem' }}
          />
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.75rem',
            ml: 'auto'
          }}>
            #{adBlock.sort_order}
          </Typography>
        </Box>

        {/* Третья строка: код рекламного блока */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.7rem',
            minWidth: '35px'
          }}>
            Код:
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'monospace',
            fontSize: '0.75rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            backgroundColor: 'grey.100',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5
          }}>
            {adBlock.code}
          </Typography>
        </Box>

        {/* Четвёртая строка: ГЕО */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.7rem',
            minWidth: '35px'
          }}>
            ГЕО:
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: '0.75rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {getGeoLabel(adBlock.geo_restrictions)}
          </Typography>
        </Box>

        {/* Пятая строка: приложения */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 1,
          width: '100%'
        }}>
          <Typography variant="caption" sx={{ 
            color: 'text.secondary',
            fontSize: '0.7rem',
            minWidth: '35px'
          }}>
            Апп:
          </Typography>
          <Typography variant="body2" sx={{ 
            fontSize: '0.75rem',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {getApplicationsLabel(adBlock.applications)}
          </Typography>
        </Box>

        {/* Шестая строка: управление */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          width: '100%',
          minHeight: '44px',
          pt: 0.5
        }}>
          {/* Кнопки управления (touch-friendly) */}
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Tooltip title="Редактировать">
              <IconButton
                size="medium"
                onClick={() => onEdit(adBlock.id)}
                sx={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.main' }
                }}
              >
                <EditIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={adBlock.is_active ? 'Отключить' : 'Включить'}>
              <IconButton
                size="medium"
                onClick={() => onToggleStatus(adBlock)}
                sx={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                  backgroundColor: adBlock.is_active ? 'warning.light' : 'success.light',
                  color: 'white',
                  '&:hover': { 
                    backgroundColor: adBlock.is_active ? 'warning.main' : 'success.main' 
                  }
                }}
              >
                {adBlock.is_active ? 
                  <VisibilityOffIcon sx={{ fontSize: '18px' }} /> : 
                  <VisibilityIcon sx={{ fontSize: '18px' }} />
                }
              </IconButton>
            </Tooltip>
            <Tooltip title="Удалить">
              <IconButton
                size="medium"
                onClick={() => onDelete(adBlock)}
                sx={{ 
                  minWidth: '44px', 
                  minHeight: '44px',
                  backgroundColor: 'error.light',
                  color: 'error.contrastText',
                  '&:hover': { backgroundColor: 'error.main' }
                }}
              >
                <DeleteIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const AdsPage = () => {
  const navigate = useNavigate();
  const [adBlocks, setAdBlocks] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [adBlockToDelete, setAdBlockToDelete] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Состояние для сортировки
  const [sortField, setSortField] = useState('sort_order');
  const [sortDirection, setSortDirection] = useState('asc');

  // Состояние для фильтров (массивы для мультиселекта)
  const [filters, setFilters] = useState({
    name: [],
    ad_type: [],
    type_sdk: [],
    code: [],
    sort_order: [],
    geo: [],
    applications: [],
    status: []
  });



  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [adBlocksResponse, applicationsResponse] = await Promise.all([
        adsAPI.getAll(),
        applicationsAPI.getAll()
      ]);
      
      setAdBlocks(adBlocksResponse.data);
      setApplications(applicationsResponse.data);
      setError('');
    } catch (err) {
      setError('Ошибка загрузки данных');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };



  const handleDelete = async () => {
    if (!adBlockToDelete) return;
    
    try {
      await adsAPI.delete(adBlockToDelete.id);
      await loadData();
      setDeleteConfirmOpen(false);
      setAdBlockToDelete(null);
    } catch (err) {
      setError('Ошибка удаления рекламного блока');
      console.error('Error deleting ad block:', err);
    }
  };

  const handleToggleStatus = async (adBlock) => {
    try {
      await adsAPI.toggle(adBlock.id);
      await loadData();
    } catch (err) {
      setError('Ошибка изменения статуса');
      console.error('Error toggling status:', err);
    }
  };

  const getAdTypeLabel = (adType) => {
    const type = AD_TYPES.find(t => t.value === adType);
    return type ? type.label : adType;
  };

  const getSdkLabel = (sdk) => {
    const sdkType = SDK_TYPES.find(s => s.value === sdk);
    return sdkType ? sdkType.label : sdk;
  };

  const getGeoLabel = (geoRestrictions) => {
    if (!geoRestrictions || geoRestrictions.type === 'world') {
      return 'Весь мир';
    }
    
    if (geoRestrictions.type === 'countries' && geoRestrictions.countries) {
      const countryNames = geoRestrictions.countries.map(code => {
        const country = COUNTRIES.find(c => c.code === code);
        return country ? country.name : code;
      });
      return countryNames.join(', ');
    }
    
    return 'Не указано';
  };

  const getApplicationsLabel = (appBundles) => {
    if (!appBundles || appBundles.length === 0) {
      return 'Все приложения';
    }
    
    const appNames = appBundles.map(bundle => {
      const app = applications.find(a => a.bundle === bundle);
      return app ? app.name : bundle;
    });
    
    return appNames.join(', ');
  };

  // Обработчик сортировки
  const handleSort = (field, direction) => {
    if (field === null) {
      setSortField('sort_order');
      setSortDirection('asc');
    } else {
      setSortField(field);
      setSortDirection(direction);
    }
  };

  // Обработчик изменения фильтра
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Получить уникальные опции для фильтра
  const getUniqueOptions = (field) => {
    const values = new Set();
    
    adBlocks.forEach(adBlock => {
      if (field === 'name') {
        values.add(adBlock.name);
      } else if (field === 'ad_type') {
        values.add(adBlock.ad_type);
      } else if (field === 'type_sdk') {
        values.add(adBlock.type_sdk);
      } else if (field === 'code') {
        values.add(adBlock.code);
      } else if (field === 'sort_order') {
        values.add(adBlock.sort_order.toString());
      } else if (field === 'geo') {
        const geoLabel = getGeoLabel(adBlock.geo_restrictions);
        values.add(geoLabel);
      } else if (field === 'applications') {
        if (!adBlock.applications || adBlock.applications.length === 0) {
          values.add('Все приложения');
        } else {
          adBlock.applications.forEach(bundle => {
            const app = applications.find(a => a.bundle === bundle);
            values.add(app ? app.name : bundle);
          });
        }
      } else if (field === 'status') {
        values.add(adBlock.is_active ? 'active' : 'inactive');
      }
    });
    
    if (field === 'name') {
      return Array.from(values).sort().map(v => ({ value: v, label: v }));
    } else if (field === 'ad_type') {
      return Array.from(values).map(v => ({ value: v, label: getAdTypeLabel(v) }));
    } else if (field === 'type_sdk') {
      return Array.from(values).map(v => ({ value: v, label: getSdkLabel(v) }));
    } else if (field === 'code') {
      return Array.from(values).sort().map(v => ({ value: v, label: v }));
    } else if (field === 'sort_order') {
      return Array.from(values).sort((a, b) => parseInt(a) - parseInt(b)).map(v => ({ value: v, label: v }));
    } else if (field === 'geo') {
      return Array.from(values).sort().map(v => ({ value: v, label: v }));
    } else if (field === 'applications') {
      return Array.from(values).sort().map(v => ({ value: v, label: v }));
    } else if (field === 'status') {
      return [
        { value: 'active', label: 'Активен' },
        { value: 'inactive', label: 'Отключен' }
      ];
    }
    return [];
  };

  // Фильтрация и сортировка данных
  const getFilteredAndSortedData = () => {
    let filtered = [...adBlocks];

    // Применяем фильтры (работа с массивами)
    if (filters.name.length > 0) {
      filtered = filtered.filter(ad => filters.name.includes(ad.name));
    }
    if (filters.ad_type.length > 0) {
      filtered = filtered.filter(ad => filters.ad_type.includes(ad.ad_type));
    }
    if (filters.type_sdk.length > 0) {
      filtered = filtered.filter(ad => filters.type_sdk.includes(ad.type_sdk));
    }
    if (filters.code.length > 0) {
      filtered = filtered.filter(ad => filters.code.includes(ad.code));
    }
    if (filters.sort_order.length > 0) {
      filtered = filtered.filter(ad => filters.sort_order.includes(ad.sort_order.toString()));
    }
    if (filters.geo.length > 0) {
      filtered = filtered.filter(ad => {
        const geoLabel = getGeoLabel(ad.geo_restrictions);
        return filters.geo.includes(geoLabel);
      });
    }
    if (filters.applications.length > 0) {
      filtered = filtered.filter(ad => {
        if (!ad.applications || ad.applications.length === 0) {
          return filters.applications.includes('Все приложения');
        }
        return ad.applications.some(bundle => {
          const app = applications.find(a => a.bundle === bundle);
          const appName = app ? app.name : bundle;
          return filters.applications.includes(appName);
        });
      });
    }
    if (filters.status.length > 0) {
      filtered = filtered.filter(ad => {
        const status = ad.is_active ? 'active' : 'inactive';
        return filters.status.includes(status);
      });
    }

    // Применяем сортировку
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'ad_type':
          aValue = getAdTypeLabel(a.ad_type).toLowerCase();
          bValue = getAdTypeLabel(b.ad_type).toLowerCase();
          break;
        case 'type_sdk':
          aValue = getSdkLabel(a.type_sdk).toLowerCase();
          bValue = getSdkLabel(b.type_sdk).toLowerCase();
          break;
        case 'code':
          aValue = a.code.toLowerCase();
          bValue = b.code.toLowerCase();
          break;
        case 'sort_order':
          aValue = a.sort_order;
          bValue = b.sort_order;
          break;
        case 'geo':
          aValue = getGeoLabel(a.geo_restrictions).toLowerCase();
          bValue = getGeoLabel(b.geo_restrictions).toLowerCase();
          break;
        case 'applications':
          aValue = getApplicationsLabel(a.applications).toLowerCase();
          bValue = getApplicationsLabel(b.applications).toLowerCase();
          break;
        case 'status':
          aValue = a.is_active ? 1 : 0;
          bValue = b.is_active ? 1 : 0;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  };

  const filteredData = getFilteredAndSortedData();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Загрузка...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Управление рекламой
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/ads/settings')}
          >
            Рекламные настройки
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/ads/new')}
          >
            Добавить рекламный блок
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Адаптивное отображение рекламных блоков: карточки на мобильных, таблица на десктопе */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <Box sx={{ 
          width: '100%', 
          maxWidth: '100vw', 
          overflow: 'hidden'
        }}>
          {adBlocks.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Рекламные блоки не найдены
              </Typography>
            </Paper>
          ) : (
            adBlocks.map((adBlock) => (
              <AdBlockCard
                key={adBlock.id}
                adBlock={adBlock}
                applications={applications}
                onEdit={(id) => navigate(`/ads/${id}`)}
                onToggleStatus={handleToggleStatus}
                onDelete={(adBlock) => {
                  setAdBlockToDelete(adBlock);
                  setDeleteConfirmOpen(true);
                }}
                getAdTypeLabel={getAdTypeLabel}
                getSdkLabel={getSdkLabel}
                getGeoLabel={getGeoLabel}
                getApplicationsLabel={getApplicationsLabel}
              />
            ))
          )}
        </Box>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <FilterableTableHeader
                  label="Название"
                  field="name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.name}
                  onFilterChange={(value) => handleFilterChange('name', value)}
                  options={getUniqueOptions('name')}
                />
                <FilterableTableHeader
                  label="Тип"
                  field="ad_type"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.ad_type}
                  onFilterChange={(value) => handleFilterChange('ad_type', value)}
                  options={getUniqueOptions('ad_type')}
                />
                <FilterableTableHeader
                  label="SDK"
                  field="type_sdk"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.type_sdk}
                  onFilterChange={(value) => handleFilterChange('type_sdk', value)}
                  options={getUniqueOptions('type_sdk')}
                />
                <FilterableTableHeader
                  label="Код"
                  field="code"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.code}
                  onFilterChange={(value) => handleFilterChange('code', value)}
                  options={getUniqueOptions('code')}
                />
                <FilterableTableHeader
                  label="Порядок"
                  field="sort_order"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.sort_order}
                  onFilterChange={(value) => handleFilterChange('sort_order', value)}
                  options={getUniqueOptions('sort_order')}
                />
                <FilterableTableHeader
                  label="ГЕО"
                  field="geo"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.geo}
                  onFilterChange={(value) => handleFilterChange('geo', value)}
                  options={getUniqueOptions('geo')}
                />
                <FilterableTableHeader
                  label="Приложения"
                  field="applications"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.applications}
                  onFilterChange={(value) => handleFilterChange('applications', value)}
                  options={getUniqueOptions('applications')}
                />
                <FilterableTableHeader
                  label="Статус"
                  field="status"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  filterValue={filters.status}
                  onFilterChange={(value) => handleFilterChange('status', value)}
                  options={getUniqueOptions('status')}
                />
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Рекламные блоки не найдены
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredData.map((adBlock) => (
                  <TableRow key={adBlock.id}>
                    <TableCell>{adBlock.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getAdTypeLabel(adBlock.ad_type)} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getSdkLabel(adBlock.type_sdk)} 
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {adBlock.code}
                      </Typography>
                    </TableCell>
                    <TableCell>{adBlock.sort_order}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getGeoLabel(adBlock.geo_restrictions)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getApplicationsLabel(adBlock.applications)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={adBlock.is_active ? 'Активен' : 'Отключен'}
                        color={adBlock.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Редактировать">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/ads/${adBlock.id}`)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.light',
                                color: 'primary.contrastText'
                              }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={adBlock.is_active ? 'Отключить' : 'Включить'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(adBlock)}
                            sx={{ 
                              color: adBlock.is_active ? 'warning.main' : 'success.main',
                              '&:hover': {
                                backgroundColor: adBlock.is_active ? 'warning.light' : 'success.light',
                                color: 'white'
                              }
                            }}
                          >
                            {adBlock.is_active ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAdBlockToDelete(adBlock);
                              setDeleteConfirmOpen(true);
                            }}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.light',
                                color: 'error.contrastText'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}



      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить рекламный блок "{adBlockToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Отмена</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdsPage;
